import {db} from "@zeg/instant/db";
import {useInitSetup} from "@zeg/instant/w/useInitSetup";
import {createContext, ReactNode, useContext, useEffect, useState} from "react";

type AuthState = ReturnType<typeof db.useAuth> & {
  initData: {isLoading: boolean; error: any; data: any} | null;
  isProfilesReady: boolean;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({children}: {children: ReactNode}) {
  const authState = db.useAuth();
  const initSetup = useInitSetup();
  const [isProfilesReady, setIsProfilesReady] = useState(false);

  const effectiveInitSetup = authState.user
    ? initSetup
    : {isLoading: false, error: null, data: null};

  // Track when profiles are ready
  useEffect(() => {
    if (
      authState.user &&
      !effectiveInitSetup.isLoading &&
      effectiveInitSetup.data?.profiles !== undefined
    ) {
      setIsProfilesReady(true);
    } else if (!authState.user) {
      setIsProfilesReady(false);
    }
  }, [authState.user, effectiveInitSetup.isLoading, effectiveInitSetup.data]);

  if (authState.isLoading || (authState.user && effectiveInitSetup.isLoading)) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{...authState, initData: effectiveInitSetup, isProfilesReady}}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error("useAuth must be used within AuthProvider");
  return auth;
}
